/**
 * The hall: a round room 38 m across under a glowing dome. A polished charcoal floor that
 * reflects a generated environment, solid curved walls north (the Study Gateway) and south
 * (the arrival arch) with a faint flowing light pattern, floor-to-ceiling panoramic windows
 * east and west, light coves at the floor and at the dome's rim, and soft pools of light on
 * the floor that the scene brightens as the visitor's journey reaches them.
 *
 * Angles are measured from north (-z) toward east (+x): a point at angle φ on the wall is
 * (R sin φ, z = -R cos φ). three.js cylinders measure θ from +z, so θ = π - φ.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TOKENS } from '../../ui/theme.ts';
import { glowMaterial, radialTexture, type TimeUniform } from './glow.ts';

export const HALL = {
  radius: 19,
  height: 15,
  /** Solid wall arcs (degrees from north) and window arcs. */
  solid: [
    [-58, 58],
    [150, 210],
  ] as [number, number][],
  windows: [
    [58, 150],
    [210, 302],
  ] as [number, number][],
};

const DEG = Math.PI / 180;

/** Floor pools of light, in the order `setPool` addresses them. */
export const POOLS = ['sculpture', 'reception', 'gateway', 'loungeEast', 'loungeWest'] as const;
export type PoolName = (typeof POOLS)[number];

export interface Hall {
  group: THREE.Group;
  floor: THREE.Mesh;
  envMap: THREE.Texture;
  setPool(name: PoolName, colour: THREE.ColorRepresentation, intensity: number): void;
  dispose(): void;
}

function arcGeometry(
  radius: number,
  height: number,
  fromDeg: number,
  toDeg: number,
  y0 = 0,
): THREE.CylinderGeometry {
  const length = (toDeg - fromDeg) * DEG;
  const g = new THREE.CylinderGeometry(
    radius,
    radius,
    height,
    Math.max(8, Math.round((toDeg - fromDeg) / 2)),
    1,
    true,
    Math.PI - toDeg * DEG,
    length,
  );
  g.translate(0, y0 + height / 2, 0);
  return g;
}

export function buildHall(renderer: THREE.WebGLRenderer, time: TimeUniform): Hall {
  const { radius: R, height: H } = HALL;
  const group = new THREE.Group();
  group.name = 'hall';
  const envTarget = makeEnvironment(renderer);
  const envMap = envTarget.texture;

  // Floor: polished charcoal stone. A cheap unlit shader, not a lit material sampling the
  // blurred environment: the floor fills half the view and that cost the Quest's fill rate
  // (it was 40 % of the lobby's frame in measurements). The look of polish comes from a sheen
  // toward grazing angles, the sculpture's light pooled in the middle, the cove's glow near
  // the wall, and a faint stone grain; pools and pathways add their light on top.
  const floor = new THREE.Mesh(
    new THREE.CircleGeometry(R + 0.2, 128).rotateX(-Math.PI / 2),
    new THREE.ShaderMaterial({
      uniforms: {
        uBase: { value: new THREE.Color('#080b10') },
        uSheen: { value: new THREE.Color('#2a4a66') },
        uGlow: { value: new THREE.Color(TOKENS.cyan) },
        uRadius: { value: R },
      },
      vertexShader: /* glsl */ `
        varying vec3 vWorld;
        void main() {
          vec4 w = modelMatrix * vec4(position, 1.0);
          vWorld = w.xyz;
          gl_Position = projectionMatrix * viewMatrix * w;
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3 uBase;
        uniform vec3 uSheen;
        uniform vec3 uGlow;
        uniform float uRadius;
        varying vec3 vWorld;
        void main() {
          vec3 v = normalize(cameraPosition - vWorld);
          float g = 1.0 - v.y;
          float sheen = g * g * g;
          float r = length(vWorld.xz) / uRadius;
          float centre = max(0.0, 1.0 - r * 2.2);
          float edge = max(0.0, (r - 0.86) * 7.0);
          float grain = fract(sin(dot(floor(vWorld.xz * 6.0), vec2(12.99, 78.23))) * 437.5) * 0.012;
          vec3 c = uBase + uSheen * sheen * 0.6 + uGlow * (centre * centre * 0.05 + edge * edge * 0.09) + grain;
          gl_FragColor = vec4(c, 1.0);
        }`,
      toneMapped: false,
    }),
  );
  floor.name = 'lobby-floor';
  group.add(floor);

  // Solid walls: deep charcoal, a soft vertical gradient and the flowing light in one opaque
  // unlit shader. (A separate additive layer over the walls doubled their fill cost, which is
  // the Quest's limit.)
  const solid = mergeGeometries(HALL.solid.map(([a, b]) => arcGeometry(R, H, a, b)))!;
  const walls = new THREE.Mesh(
    solid,
    new THREE.ShaderMaterial({
      uniforms: { uTime: time, uColour: { value: new THREE.Color(TOKENS.cyan) } },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform vec3 uColour;
        varying vec2 vUv;
        void main() {
          // Cheap per pixel: the walls fill much of the view, and fill is the Quest's limit.
          // Golden-ratio hash, linear ramps, a triangle wave and a power by multiplication.
          float y = vUv.y;
          vec3 base = mix(vec3(0.045, 0.055, 0.072), vec3(0.075, 0.09, 0.115), clamp(y * 2.0, 0.0, 1.0));
          base *= 1.0 - 0.25 * clamp((y - 0.6) * 2.5, 0.0, 1.0);
          float height = clamp((y - 0.02) * 10.0, 0.0, 1.0) * clamp((0.95 - y) * 2.5, 0.0, 1.0);
          float bands = abs(fract(vUv.x * 9.55 + uTime * 0.024) - 0.5);
          float glow = bands * 0.05;
          float laneF = y * 46.0;
          float lane = floor(laneF);
          float r = fract(lane * 0.618034 + 0.31);
          if (r > 0.62) {
            float inLane = clamp(1.0 - (abs(fract(laneF) - 0.5) - 0.38) * 8.3, 0.0, 1.0);
            float dir = r > 0.8 ? -1.0 : 1.0;
            float x = fract(vUv.x * (3.0 + r * 5.0) - uTime * (0.006 + r * 0.02) * dir);
            float t = 1.0 - x;
            float t2 = t * t;
            float t4 = t2 * t2;
            float comet = min(x * 66.0, 1.0) * t4 * t4 * t2;
            glow += inLane * comet * 0.55;
          }
          gl_FragColor = vec4(base + uColour * glow * height, 1.0);
        }`,
      side: THREE.BackSide,
      toneMapped: false,
    }),
  );
  walls.name = 'lobby-walls';
  group.add(walls);

  // Windows: faint reflective glass with slim silver mullions. Unlit with a cheap reflection:
  // the glass covers half the hall's walls, so its per-pixel cost matters on the Quest.
  const glass = new THREE.Mesh(
    mergeGeometries(HALL.windows.map(([a, b]) => arcGeometry(R, H, a, b)))!,
    new THREE.MeshBasicMaterial({
      color: '#1b3348',
      envMap,
      reflectivity: 0.35,
      transparent: true,
      opacity: 0.14,
      depthWrite: false,
      side: THREE.DoubleSide,
      forceSinglePass: true,
      fog: false,
    }),
  );
  glass.name = 'lobby-glass';
  glass.renderOrder = 5;
  group.add(glass);
  const mullionAngles: number[] = [];
  for (const [a, b] of HALL.windows)
    for (let d = a; d <= b + 0.01; d += 7.666) mullionAngles.push(d);
  const mullions = new THREE.InstancedMesh(
    new THREE.BoxGeometry(0.06, H, 0.12).translate(0, H / 2, 0),
    new THREE.MeshPhongMaterial({ color: TOKENS.silver, specular: '#ffffff', shininess: 80 }),
    mullionAngles.length,
  );
  const dummy = new THREE.Object3D();
  mullionAngles.forEach((d, i) => {
    const phi = d * DEG;
    dummy.position.set((R - 0.05) * Math.sin(phi), 0, -(R - 0.05) * Math.cos(phi));
    dummy.rotation.set(0, -phi, 0);
    dummy.updateMatrix();
    mullions.setMatrixAt(i, dummy.matrix);
  });
  mullions.name = 'lobby-mullions';
  group.add(mullions);

  // Light coves: along the foot of the wall and around the dome's rim.
  const cove = new THREE.Mesh(
    new THREE.CylinderGeometry(R - 0.08, R - 0.08, 0.05, 128, 1, true).translate(0, 0.03, 0),
    glowMaterial(TOKENS.cyan, 0.9),
  );
  cove.name = 'lobby-cove';
  group.add(cove);
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(R - 0.25, 0.05, 6, 160).rotateX(Math.PI / 2).translate(0, H - 0.1, 0),
    glowMaterial(TOKENS.cyan, 0.3),
  );
  rim.name = 'lobby-rim';
  group.add(rim);

  // The dome: a soft glow that seems to rise without end, with faint rings drifting outward.
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(R, 64, 18, 0, Math.PI * 2, 0, Math.PI / 2)
      .scale(1, 0.32, 1)
      .translate(0, H, 0),
    new THREE.ShaderMaterial({
      uniforms: { uTime: time },
      vertexShader: /* glsl */ `
        varying vec3 vLocal;
        void main() {
          vLocal = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        varying vec3 vLocal;
        void main() {
          float r = clamp(length(vLocal.xz) / ${R.toFixed(1)}, 0.0, 1.0);
          float core = pow(1.0 - r, 1.9);
          vec3 base = mix(vec3(0.05, 0.075, 0.11), vec3(0.66, 0.88, 1.0), core);
          base += vec3(0.95, 0.98, 1.0) * pow(1.0 - r, 9.0) * 0.6;
          float rings = smoothstep(0.03, 0.0, abs(fract(r * 7.0 - uTime * 0.015) - 0.5) - 0.465);
          base += vec3(0.25, 0.75, 1.0) * rings * 0.05 * (1.0 - r);
          gl_FragColor = vec4(base, 1.0);
        }`,
      side: THREE.BackSide,
      toneMapped: false,
      fog: false,
    }),
  );
  dome.name = 'lobby-dome';
  group.add(dome);

  // Pools of light on the floor (one instanced mesh; the scene sets colour and strength).
  const poolSpots: Record<PoolName, { x: number; z: number; r: number }> = {
    sculpture: { x: 0, z: 0, r: 6.5 },
    reception: { x: 0, z: 12, r: 4.8 },
    gateway: { x: 0, z: -14.4, r: 4.2 },
    loungeEast: { x: 12, z: 1, r: 3.6 },
    loungeWest: { x: -12, z: 1, r: 3.6 },
  };
  const pools = new THREE.InstancedMesh(
    new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2),
    glowMaterial('#ffffff', 1, radialTexture()),
    POOLS.length,
  );
  POOLS.forEach((name, i) => {
    const s = poolSpots[name];
    dummy.position.set(s.x, 0.012, s.z);
    dummy.rotation.set(0, 0, 0);
    dummy.scale.set(s.r, 1, s.r);
    dummy.updateMatrix();
    pools.setMatrixAt(i, dummy.matrix);
    pools.setColorAt(i, new THREE.Color('#000000'));
  });
  pools.name = 'lobby-pools';
  pools.renderOrder = 2;
  group.add(pools);
  const tmp = new THREE.Color();
  const setPool = (name: PoolName, colour: THREE.ColorRepresentation, intensity: number): void => {
    pools.setColorAt(POOLS.indexOf(name), tmp.set(colour).multiplyScalar(intensity));
    pools.instanceColor!.needsUpdate = true;
  };

  // The arrival arch on the south wall: an outline of light where visitors come in.
  const archPath = new THREE.CurvePath<THREE.Vector3>();
  const aw = 1.7;
  const ah = 3.6;
  const az = R - 0.35;
  archPath.add(new THREE.LineCurve3(new THREE.Vector3(-aw, 0, az), new THREE.Vector3(-aw, ah, az)));
  archPath.add(
    new THREE.QuadraticBezierCurve3(
      new THREE.Vector3(-aw, ah, az),
      new THREE.Vector3(0, ah + aw * 1.1, az),
      new THREE.Vector3(aw, ah, az),
    ),
  );
  archPath.add(new THREE.LineCurve3(new THREE.Vector3(aw, ah, az), new THREE.Vector3(aw, 0, az)));
  const arch = new THREE.Mesh(
    new THREE.TubeGeometry(archPath, 80, 0.035, 6, false),
    glowMaterial(TOKENS.cyanSoft, 0.9),
  );
  arch.name = 'lobby-arch';
  group.add(arch);

  return {
    group,
    floor,
    envMap,
    setPool,
    dispose: () => {
      envTarget.dispose();
    },
  };
}

/**
 * The floor and glass reflect this: a dark room with a bright dome, a ring of light where
 * the sculpture turns, and cool light from the windows east and west. Generated once per
 * lobby entry through PMREM.
 */
function makeEnvironment(renderer: THREE.WebGLRenderer): THREE.WebGLRenderTarget {
  const scene = new THREE.Scene();
  const sky = new THREE.Mesh(
    new THREE.SphereGeometry(40, 32, 16),
    new THREE.ShaderMaterial({
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          vDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          float up = max(vDir.y, 0.0);
          vec3 c = mix(vec3(0.02, 0.025, 0.035), vec3(0.18, 0.26, 0.36), smoothstep(0.0, 0.6, up));
          c += vec3(1.4, 1.7, 2.0) * pow(up, 12.0);
          float side = abs(vDir.x) * (1.0 - abs(vDir.y));
          c += vec3(0.10, 0.22, 0.38) * smoothstep(0.55, 0.95, side) * step(-0.05, vDir.y);
          gl_FragColor = vec4(c, 1.0);
        }`,
      side: THREE.BackSide,
    }),
  );
  scene.add(sky);
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(2.6, 0.12, 8, 64).rotateX(Math.PI / 2.4).translate(0, 4.2, 0),
    new THREE.MeshBasicMaterial({ color: new THREE.Color(TOKENS.cyan).multiplyScalar(5) }),
  );
  scene.add(ring);
  const cove = new THREE.Mesh(
    new THREE.TorusGeometry(18.8, 0.08, 6, 96).rotateX(Math.PI / 2).translate(0, 0.05, 0),
    new THREE.MeshBasicMaterial({ color: new THREE.Color(TOKENS.cyan).multiplyScalar(3) }),
  );
  scene.add(cove);
  const pmrem = new THREE.PMREMGenerator(renderer);
  const target = pmrem.fromScene(scene, 0.02);
  pmrem.dispose();
  scene.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) {
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
  });
  return target;
}
